/**
 * "Enviar pendências corrigidas" (plano de 2026-10-30): o pop-up que
 * recebe a planilha de pendências de volta, corrigida no Excel.
 *
 * Não mostra recibo: o recibo é a própria página (a faixa de envios no
 * topo de "Linhas com erro", os baldes "Reprocessando…" e a tabela
 * recarregada). Falha — 422, 429, 409 — fica DENTRO do pop-up e nunca o
 * fecha: a pessoa corrige o arquivo e tenta de novo sem perder a tela.
 */

import { FileUp, LoaderCircle } from "lucide-react";
import { useState } from "react";

import {
  apiClient,
  type CorrectionSheetSubmission
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import {
  ConfirmDialogDetailsBox,
  ConfirmDialogFrame
} from "@/shared/components/ConfirmDialog";

type Props = {
  token: string;
  uploadId: number;
  onClose: () => void;
  /** Depois do 201; quem chama recarrega a página e a faixa. */
  onSubmitted: (submission: CorrectionSheetSubmission) => void | Promise<void>;
};

const HOW_TO = [
  "Baixe as pendências e edite a aba Linhas pendentes — só o que você mudar vale.",
  "A coluna Linha é o mesmo número que a tabela \"Linhas com erro\" mostra — não a mude.",
  "Texto da matéria vira o texto da matéria (apagar o que você colou o limpa).",
  "Ação: reprocessar (ou em branco) ou descartar. Linha sem mudança e sem ação fica como está.",
  "Texto cortado pelo site (passa do limite de uma célula do Excel) se edita pela tela, linha a linha."
];

export function CorrectionSheetDialog({ token, uploadId, onClose, onSubmitted }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!file || sending) return;
    setSending(true);
    setError(null);
    try {
      const submission = await apiClient.submitCorrectionSheet(token, uploadId, file);
      await onSubmitted(submission);
      onClose();
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível enviar a planilha."));
      setSending(false);
    }
  };

  return (
    <ConfirmDialogFrame
      title="Enviar pendências corrigidas"
      icon={FileUp}
      closeDisabled={sending}
      onClose={onClose}
      onSubmit={() => void send()}
      actions={
        <>
          <button type="button" className="secondary-button" onClick={onClose} disabled={sending}>
            Cancelar
          </button>
          <button type="submit" className="primary-button" disabled={!file || sending}>
            {sending ? <LoaderCircle className="spin" size={16}/> : null}
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </>
      }
    >
      <p>
        Suba a planilha de pendências que você baixou aqui, já corrigida. As linhas
        mudadas voltam pra fila e são reprocessadas.
      </p>
      <ConfirmDialogDetailsBox details={{ title: "Como preencher", items: HOW_TO }}/>
      <label className="account-field">
        <span>Planilha (.xlsx ou .csv)</span>
        <input
          type="file"
          accept=".xlsx,.csv"
          onChange={event => setFile(event.target.files?.[0] ?? null)}
          disabled={sending}
        />
      </label>
      {error && <div className="account-feedback error" aria-live="polite">{error}</div>}
    </ConfirmDialogFrame>
  );
}
